import React from "react";
// ANT DESIGN COMPONENTS
import { Button, Tooltip } from "antd";

/**
 * The one command button used by all six action behaviour components.
 *
 * Every button previously carried its own copy of `style={{ backgroundColor:
 * colorAction, width: "210px" }}` plus a hand-rolled disabled class — and the
 * class two of them used, `buttonStyle__basicButtonActionInactive`, was never
 * defined in the stylesheet, so "disabled" looked identical to enabled apart
 * from antd's own opacity. Centralising it also fixes the configured colour
 * staying fully saturated on a disabled control: the inline background is
 * applied only while the button is live, and the disabled state falls back to
 * the theme's neutral surface so it reads as inert without relying on colour.
 */
export default function ActionCommandButton({
  label,
  color = undefined,
  disabled = false,
  loading = false,
  disabledReason = undefined,
  countdown = undefined,
  countdownLabel = undefined,
  outlined = false,
  danger = false,
  onClick = undefined,
  onMouseDown = undefined,
  onMouseUp = undefined,
  onMouseLeave = undefined,
  onTouchStart = undefined,
  onTouchEnd = undefined,
}: {
  /** The operator-configured label. Never substituted with "ON"/"OFF". */
  label: string;
  /** Configured action colour. Applied only while the button is enabled. */
  color?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Tooltip shown while disabled, so the block has a stated reason. */
  disabledReason?: string;
  /** Live countdown in seconds; null/undefined renders no timer. */
  countdown?: number | null;
  /** Label to show in place of `label` while counting down. */
  countdownLabel?: string;
  /** Secondary/cancel styling — outline rather than a filled colour. */
  outlined?: boolean;
  /** Marks a cancel/abort command, so intent reads without relying on colour. */
  danger?: boolean;
  onClick?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onMouseLeave?: () => void;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
}) {
  const counting = typeof countdown === "number";
  const shownLabel = counting && countdownLabel ? countdownLabel : label;

  const variantClass = (() => {
    if (disabled) return "actionCard__button--disabled";
    if (outlined || !color) return "";
    return "actionCard__button--filled";
  })();

  const button = (
    <Button
      className={`actionCard__button ${variantClass}`.trim()}
      style={!disabled && color && !outlined ? { backgroundColor: color } : undefined}
      danger={danger}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <span className="actionCard__buttonLabel">{shownLabel}</span>
      {counting && <span className="actionCard__buttonTimer">{countdown}s</span>}
    </Button>
  );

  // antd suppresses pointer events on a disabled button, so the tooltip has to
  // hang off a wrapper or it never opens — the reason for the block would be
  // unreachable exactly when the operator needs it.
  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason}>
        <span className="actionCard__buttonWrap">{button}</span>
      </Tooltip>
    );
  }

  return button;
}
